/**
 * Search component.
 * @module components/theme/Search/Search
 */

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { asyncConnect } from '@plone/volto/helpers/AsyncConnect';
import { FormattedMessage } from 'react-intl';
import { createPortal } from 'react-dom';
import { Container, Pagination, Header } from 'semantic-ui-react';
import qs from 'query-string';
import classNames from 'classnames';
import { defineMessages, injectIntl } from 'react-intl';
import config from '@plone/volto/registry';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { searchContent } from '@plone/volto/actions/search/search';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import VoltoIcon from '@plone/volto/components/theme/Icon/Icon';
import SearchTag from './SearchTag';
import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';
import DateInfo from '../DateInfo';
import Icon from '../Icon';
import Button from '../Button';
import cx from 'classnames';

const messages = defineMessages({
  Search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
});

export const defaultSearchConfig = {
  use_site_search_settings: 1, // Configuração global que deve ser aplicada em todas as buscas
  metadata_fields: [
    'modified',
    'effective',
    'created',
    'review_state',
    // 'head_title',
  ],
};

/**
 * Search class.
 * @class SearchComponent
 * @extends Component
 */
class Search extends Component {
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    searchContent: PropTypes.func.isRequired,
    searchableText: PropTypes.string,
    subject: PropTypes.string,
    path: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        '@id': PropTypes.string,
        '@type': PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
    ),
    pathname: PropTypes.string.isRequired,
  };

  /**
   * Default properties.
   * @property {Object} defaultProps Default properties.
   * @static
   */
  static defaultProps = {
    items: [],
    searchableText: null,
    subject: null,
    path: null,
  };

  constructor(props) {
    super(props);
    this.defaultPageSize = config.settings.defaultPageSize;
    this.state = { currentPage: 1, isClient: false, active: 'relevance' };
  }

  /**
   * Component did mount
   * @method componentDidMount
   * @returns {undefined}
   */
  componentDidMount() {
    this.doSearch();
    this.setState({ isClient: true });
  }

  /**
   * Component will receive props
   * @method componentWillReceiveProps
   * @param {Object} nextProps Next properties
   * @returns {undefined}
   */
  UNSAFE_componentWillReceiveProps = (nextProps) => {
    if (this.props.location.search !== nextProps.location.search) {
      this.doSearch();
    }
  };

  /**
   * Search based on the given searchableText, subject and path.
   * @method doSearch
   * @param {string} searchableText The searchable text string
   * @param {string} subject The subject (tag)
   * @param {string} path The path to restrict the search to
   * @returns {undefined}
   */

  doSearch = () => {
    const options = qs.parse(this.props.history.location.search);
    this.setState({ currentPage: 1 });
    this.props.searchContent('', {
      ...defaultSearchConfig,
      b_size: this.defaultPageSize,
      ...options,
    });
  };

  handleQueryPaginationChange = (e, { activePage }) => {
    window.scrollTo(0, 0);
    let options = qs.parse(this.props.history.location.search);

    this.setState({ currentPage: activePage }, () => {
      this.props.searchContent('', {
        ...defaultSearchConfig,
        b_size: this.defaultPageSize,
        ...options,
        b_start:
          (this.state.currentPage - 1) *
          (options.b_size || this.defaultPageSize),
      });
    });
  };

  onSortChange = (event, sort_order) => {
    let options = qs.parse(this.props.history.location.search);
    options.sort_on = event.target.name;
    options.sort_order = sort_order || 'ascending';
    if (event.target.name === 'relevance') {
      delete options.sort_on;
      delete options.sort_order;
    }
    let searchParams = qs.stringify(options);
    this.setState({ currentPage: 1, active: event.target.name }, () => {
      // eslint-disable-next-line no-restricted-globals
      this.props.history.replace({
        search: searchParams,
      });
    });
  };

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    const options = qs.parse(this.props.history.location.search);

    return (
      <>
        <Helmet title={this.props.intl.formatMessage(messages.Search)} />
        <Container id="page-search">
          <div className="container">
            <article id="content">
              <header>
                <h1 className="documentFirstHeading">
                  {this.props.searchableText ? (
                    <FormattedMessage
                      id="Search results for {term}"
                      defaultMessage="Search results for {term}"
                      values={{
                        term: <q>{this.props.searchableText}</q>,
                      }}
                    />
                  ) : this.props.subject ? (
                    <FormattedMessage
                      id="Search results for tag {term}"
                      defaultMessage="Search results for tag {term}"
                      values={{
                        term: <SearchTag label={this.props.subject} />,
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id="Search results"
                      defaultMessage="Search results"
                    />
                  )}
                </h1>

                {this.props.search?.items_total > 0 ? (
                  <div className="items_total">
                    <div className="value">
                      {this.props.search.items_total}{' '}
                      <FormattedMessage
                        id="results found"
                        defaultMessage="results"
                      />
                    </div>
                    <Header>
                      <Header.Content className="header-content">
                        <div className="sort-by">
                          <FormattedMessage
                            id="Sort By:"
                            defaultMessage="Sort by:"
                          />
                        </div>
                        <Button
                          onClick={(event) => {
                            this.onSortChange(event);
                          }}
                          name="relevance"
                          small
                          link
                          className={classNames({
                            'button-active': this.state.active === 'relevance',
                          })}
                        >
                          <Icon name="ads_click" />
                          <FormattedMessage
                            id="Relevance"
                            defaultMessage="Relevance"
                          />
                        </Button>
                        <Button
                          onClick={(event) => {
                            this.onSortChange(event);
                          }}
                          name="sortable_title"
                          small
                          link
                          className={classNames({
                            'button-active':
                              this.state.active === 'sortable_title',
                          })}
                        >
                          <Icon name="text_rotate_vertical" />
                          <FormattedMessage id="Title" defaultMessage="Title" />
                        </Button>
                        <Button
                          onClick={(event) => {
                            this.onSortChange(event, 'reverse');
                          }}
                          name="effective"
                          small
                          link
                          className={classNames({
                            'button-active': this.state.active === 'effective',
                          })}
                        >
                          <Icon name="search_activity" />
                          <FormattedMessage id="Date" defaultMessage="Date" />
                        </Button>
                      </Header.Content>
                    </Header>
                  </div>
                ) : (
                  <div>
                    <FormattedMessage
                      id="No results found"
                      defaultMessage="No results found"
                    />
                  </div>
                )}
              </header>
              <section id="content-core">
                {this.props.items.map((item) => (
                  <UniversalLink
                    item={item}
                    key={item['@id']}
                    className="search-item"
                  >
                    <div className="content">
                      <h2
                        className={cx('title', {
                          [`review-state-${item.review_state}`]:
                            item.review_state,
                        })}
                      >
                        {item.title}
                      </h2>
                      <DateInfo
                        properties={item}
                        hideModifiedDate
                        hidePublishedLabel
                        hideReviewState
                      />
                      {item.description && (
                        <div className="description">{item.description}</div>
                      )}
                    </div>
                  </UniversalLink>
                ))}

                {this.props.search?.batching && (
                  <div className="search-footer">
                    <Pagination
                      activePage={this.state.currentPage}
                      totalPages={Math.ceil(
                        this.props.search.items_total /
                          (options.b_size || this.defaultPageSize),
                      )}
                      onPageChange={this.handleQueryPaginationChange}
                      firstItem={null}
                      lastItem={null}
                      prevItem={{
                        content: (
                          <VoltoIcon name={paginationLeftSVG} size="18px" />
                        ),
                        icon: true,
                        'aria-disabled': !this.props.search.batching.prev,
                        className: !this.props.search.batching.prev
                          ? 'disabled'
                          : null,
                      }}
                      nextItem={{
                        content: (
                          <VoltoIcon name={paginationRightSVG} size="18px" />
                        ),
                        icon: true,
                        'aria-disabled': !this.props.search.batching.next,
                        className: !this.props.search.batching.next
                          ? 'disabled'
                          : null,
                      }}
                    />
                  </div>
                )}
              </section>
            </article>
          </div>
        </Container>
        {this.state.isClient &&
          createPortal(
            <Toolbar
              pathname={this.props.pathname}
              hideDefaultViewButtons
              inner={<span />}
            />,
            document.getElementById('toolbar'),
          )}
      </>
    );
  }
}

export const __test__ = compose(
  injectIntl,
  connect(
    (state, props) => ({
      items: state.search.items,
      searchableText: qs.parse(props.history.location.search).SearchableText,
      subject: qs.parse(props.history.location.search).Subject,
      pathname: props.history.location.pathname,
    }),
    { searchContent },
  ),
)(Search);

export default compose(
  injectIntl,
  connect(
    (state, props) => ({
      items: state.search.items,
      searchableText: qs.parse(props.history.location.search).SearchableText,
      subject: qs.parse(props.history.location.search).Subject,
      pathname: props.location.pathname,
    }),
    { searchContent },
  ),
  asyncConnect([
    {
      key: 'search',
      promise: ({ location, store: { dispatch } }) =>
        dispatch(
          searchContent('', {
            ...qs.parse(location.search),
            ...defaultSearchConfig,
          }),
        ),
    },
  ]),
)(Search);
